import { config } from '../config';
import { db } from '../data/harnessDb';
import { StorageError } from '../app/errors';
import { log } from '../app/logger';
import type { AudioRunRecord } from '../types';

/**
 * Persists completed U-1 spike runs.
 *
 * Same reasoning as U-0's `resultsStore`, and the same pattern (`db-driven-state-over-in-memory`),
 * with one addition that matters more here: **an audio run is taken across an interruption.** The
 * AUD-3 evidence is produced by backgrounding the app or taking a phone call, which is precisely
 * the condition that discards React state. Holding results in memory would lose the measurement to
 * the exact event the measurement is about.
 *
 * The audio itself lives in `audioSegments` (see `data/audioSpikeStore.ts`); these rows carry only
 * statistics and verdicts, so a run is a few KB.
 */

const MODULE = 'audio-results';

/** @throws {StorageError} */
export async function saveAudioRun(run: AudioRunRecord): Promise<void> {
  const handle = db();
  try {
    await handle.audioRuns.put({ id: run.id, startedAt: run.startedAt, payload: run });
    await trimHistory();
    log.info(MODULE, 'spike run persisted', { id: run.id, segments: run.segments.length });
  } catch (cause) {
    throw new StorageError(
      'Could not persist the audio spike run. Export it now before navigating away — it is only in memory.',
      { cause, context: { id: run.id } },
    );
  }
}

/** Newest first. @throws {StorageError} */
export async function loadAudioRuns(): Promise<AudioRunRecord[]> {
  const handle = db();
  try {
    const rows = await handle.audioRuns.orderBy('startedAt').reverse().toArray();
    return rows.map((r) => r.payload as AudioRunRecord);
  } catch (cause) {
    throw new StorageError('Could not read stored audio spike runs.', { cause });
  }
}

/** @throws {StorageError} */
export async function deleteAudioRun(id: string): Promise<void> {
  const handle = db();
  try {
    await handle.audioRuns.delete(id);
  } catch (cause) {
    throw new StorageError('Could not delete the audio spike run.', { cause, context: { id } });
  }
}

async function trimHistory(): Promise<void> {
  const handle = db();
  const total = await handle.audioRuns.count();
  const excess = total - config.AUDIO_RUN_HISTORY_CAP;
  if (excess <= 0) return;
  const oldest = await handle.audioRuns.orderBy('startedAt').limit(excess).primaryKeys();
  await handle.audioRuns.bulkDelete(oldest);
  log.info(MODULE, 'trimmed spike run history', { removed: oldest.length });
}
